/**
 * [INPUT]: 依赖 React 页面传入的 className、日期、数字与浏览器 Web Crypto 能力
 * [OUTPUT]: 对外提供样式合并、格式化、展示辅助函数和兼容局域网 WebView 的客户端 ID
 * [POS]: admin-web 共享工具层，被 UI 与页面消费，不持有业务状态
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */

export function cn(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

let clientIdCounter = 0;

/**
 * 为幂等引用和前端列表 key 生成客户端 ID。
 * 某些局域网 WebView 暴露 crypto，但没有 randomUUID；优先使用原生实现，
 * 其次使用 getRandomValues，最后才使用带时间和递增序号的非安全 ID。
 */
export function createClientId() {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  if (typeof cryptoApi?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  clientIdCounter += 1;
  return `client-${Date.now().toString(16)}-${clientIdCounter.toString(16)}`;
}

export function formatNumber(value: number) {
  return new Intl.NumberFormat('zh-CN').format(value);
}

export function formatDate(value: string | null, withTime = false) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(new Date(value));
}

export function relativeDate(value: string) {
  const difference = Date.now() - new Date(value).getTime();
  const minutes = Math.floor(difference / 60_000);
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  return `${days} 天前`;
}

export function maskPhone(phone: string) {
  return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
}

export function titleCaseStatus(status: string) {
  const map: Record<string, string> = {
    published: '已发布',
    draft: '草稿',
    paused: '已暂停',
    staged: '待发布',
    rolled_back: '已回滚',
    active: '正常',
    blocked: '已封禁',
    pending: '待绑定',
    completed: '已完成',
    streaming: '流式中',
    failed: '失败',
    grant: '赠送',
    consume: '消费',
    release: '释放',
    refund: '退款',
    adjustment: '调整',
  };
  return map[status] ?? status;
}
